/// <amd-module name="Wit/QueriesHub/Common/Components/QueryMoreButton" />
import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import * as React from "react";
import { WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IExpandable } from "VSSUI/Expandable";
import { IMenuItem, MenuItemType, MoreButton } from "VSSUI/Menu";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { QueryItem } from "Wit/Common/QueryUtils/Models";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import * as Resources from "Wit/QueriesHub/Resources";
import { AddToDashboardPanel } from "./AddToDashboardPanel";
import { DeleteDialog } from "./DeleteDialog";

export class IQueryMoreButtonProps {
    className?: string;
    item: QueryItem;
}

const enum DialogType {
    DeleteDialog,
    AddToDashboard
}

const QueryScalarContribution = "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.QueryScalarWidget";
const SecurityNameSpaceIdForWorkItems = "71356614-AAD7-4757-8F2C-0FB3BFF6F680";

export class QueryMoreButton extends VssComponent<IQueryMoreButtonProps> implements IExpandable {
    private _favoritesService: IFavoritesService;
    private _historyService: IVssHistoryService;
    private _dialogOpen: ObservableValue<DialogType | undefined> = new ObservableValue(undefined);
    private _selectedQueryItem: QueryItem;
    private _listService: IQueryListService;
    private _projectId: string = "";
    private _teams: WebApiTeam[] | undefined = undefined;
    private _moreButton = React.createRef<MoreButton>();

    private menuItems = new ObservableCollection<IMenuItem>();
    private _menuItems = new ObservableArray<IMenuItem>();

    constructor(props: IQueryMoreButtonProps, context: IVssComponentContext) {
        super(props, context);
        this._favoritesService = this.context.pageContext.getService<IFavoritesService>("IFavoritesService");
        this._historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._listService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
        const pageData = getTFSPageData(this.context.pageContext);
        if (pageData && pageData.project) {
            this._projectId = pageData.project?.id;
        }

        this._getTeams();
        this._getMenuPropsForItem();
        this.menuItems.push(this._menuItems);
        this.menuItems.push(
            getMenuItems(
                this.context.pageContext,
                "ms.vss-work-web.work-item-query-menu",
                {
                    query: this.props.item
                },
                undefined,
                true
            )
        );
    }

    public componentDidUpdate(prevProps: Readonly<IQueryMoreButtonProps>): void {
        if (prevProps.item.name !== this.props.item.name) {
            this._getMenuPropsForItem();
        }
    }

    public render() {
        return (
            <>
                <UncheckedObserver
                    favoriteGroups={{
                        observableValue: this._listService.favoriteGroups,
                        filter: this._updateMoreButton
                    }}
                    queryTree={{
                        observableValue: this._listService.queryTree,
                        filter: () => {
                            this._getMenuPropsForItem();
                            return true;
                        }
                    }}
                >
                    <MoreButton
                        className={this.props.className}
                        contextualMenuProps={{
                            menuProps: {
                                id: "queryitem-menu",
                                items: this.menuItems
                            }
                        }}
                        hideDropdownIcon={true}
                        ref={this._moreButton}
                        subtle={true}
                    />
                </UncheckedObserver>

                <Observer dialogOpen={this._dialogOpen}>
                    {(observableProps: { dialogOpen: DialogType | undefined }) => {
                        if (observableProps.dialogOpen === DialogType.DeleteDialog) {
                            return (
                                <DeleteDialog
                                    onDismiss={this._onDialogDismiss}
                                    onDelete={this._onDeleteItem}
                                    pageContext={this.context.pageContext}
                                    projectId={this._projectId}
                                    query={this._selectedQueryItem}
                                />
                            );
                        } else if (observableProps.dialogOpen === DialogType.AddToDashboard) {
                            const widgetOptions = this._createWidgetOptions();
                            return (
                                <AddToDashboardPanel
                                    onDismiss={this._onDialogDismiss}
                                    projectId={this._projectId}
                                    widgetOptions={widgetOptions}
                                    telemetryProps={{
                                        parentPageName: "QueriesHub-QueryMoreButton",
                                    }} />
                            );
                        } else {
                            return null;
                        }
                    }}
                </Observer>
            </>
        );
    }

    public expand() {
        if (this._moreButton.current) {
            this._moreButton.current.expand();
        }
    }

    public collapse() {
        if (this._moreButton.current) {
            this._moreButton.current.collapse();
        }
    }

    private _createWidgetOptions() {
        return {
            name: this.props.item.name,
            contributionId: QueryScalarContribution,
            settings: JSON.stringify({
                queryId: this.props.item.id,
                queryName: this.props.item.name
            }),
            size: { columnSpan: 1, rowSpan: 1 }
        };
    }

    private _addTeamFavorite = async (queryItem: QueryItem, teamId: string, teamName: string) => {
        const scope: ArtifactScope = {
            id: this._projectId,
            name: "",
            type: QueriesHubConstants.FavoriteArtifactScopeType
        };
        const newFavorite = {
            artifactId: queryItem.id,
            artifactName: queryItem.name,
            artifactScope: scope,
            artifactType: FavoriteTypes.WIT_QUERYITEM
        } as Favorite;

        try {
            this._listService.addTeamFavorite(queryItem, teamId, teamName);
            await this._favoritesService.addTeamFavorite(teamId, newFavorite);
        } catch (ex) {
            // set the error and favorite value back
            reportError(this.context.pageContext, "team-favorite-query", ex?.message);
            this._listService.removeTeamFavorite(queryItem, teamId, teamName);
        }
    };

    private _removTeamFavorite = async (queryItem: QueryItem, teamId: string, teamName: string) => {
        this._listService.removeTeamFavorite(queryItem, teamId, teamName);

        const scope: ArtifactScope = {
            id: this._projectId,
            name: "",
            type: QueriesHubConstants.FavoriteArtifactScopeType
        };
        const favorites = await this._favoritesService.getTeamFavorites(teamId, FavoriteTypes.WIT_QUERYITEM, scope.type, scope.id, false);

        // this *should* be a single favorite
        const matchedFavorites = favorites.value.filter(favorite => favorite.artifactId === queryItem.id);
        for (const favorite of matchedFavorites) {
            try {
                await this._favoritesService.removeTeamFavorite(teamId, favorite);
            } catch (ex) {
                // set the error and favorite value back
                reportError(this.context.pageContext, "unfavorite-team-query", ex?.message);
                this._listService.addTeamFavorite(queryItem, teamId, teamName);
            }
        }
    };

    private _getMenuPropsForItem = async () => {
        const menuItems = [];
        const item = this.props.item;
        if (!item.isEmptyFolderContext) {
            if (item.isFolder) {
                menuItems.push(
                    {
                        id: "new-query",
                        data: item,
                        groupKey: "folder-actions",
                        iconProps: { iconName: "Add" },
                        text: Resources.NewQueryCommand,
                        onActivate: (item: IMenuItem) => {
                            this._listService.onEditorClick(item.id, true, item.data.id);
                        }
                    },
                    {
                        id: "new-query-folder",
                        data: item,
                        groupKey: "folder-actions",
                        text: Resources.NewFolder,
                        onActivate: (item: IMenuItem) => {
                            this._listService.onNewFolderClick(QueriesHubConstants.MoreButtonId + item.id, item.data, this._onDialogDismiss);
                        }
                    }
                );

                const state = this._historyService.getState();
                const depth = item.depth || 0;
                if (depth !== 0 || state["view"] === QueriesHubConstants.FolderView) {
                    menuItems.push(
                        {
                            id: "subfolder-divider",
                            itemType: MenuItemType.Divider
                        },
                        {
                            id: "rename-query-folder",
                            data: item,
                            groupKey: "folder-actions",
                            iconProps: { iconName: "Rename" },
                            text: Resources.RenameQueryFolderTitle,
                            onActivate: (item: IMenuItem) => {
                                this._listService.onRenameItemClick(QueriesHubConstants.MoreButtonId + item.id, item.data, this._onDialogDismiss);
                            }
                        },
                        {
                            id: "delete-query-folder",
                            data: item,
                            groupKey: "folder-actions",
                            iconProps: { iconName: "Delete" },
                            text: Resources.DeleteFolder,
                            onActivate: (item: IMenuItem) => {
                                this._selectedQueryItem = item.data;
                                this._dialogOpen.value = DialogType.DeleteDialog;
                            }
                        }
                    );
                }
            } else {
                menuItems.push(
                    {
                        id: "run-query",
                        data: item,
                        groupKey: "query-actions",
                        iconProps: { iconName: "Play" },
                        text: Resources.RunQueryCommand,
                        onActivate: (item: IMenuItem) => {
                            this._listService.onQueryClick(item.id, item.data.id);
                        }
                    },
                    {
                        id: "edit-query",
                        data: item,
                        groupKey: "query-actions",
                        iconProps: { iconName: "Edit" },
                        text: Resources.EditQuery,
                        onActivate: (item: IMenuItem) => {
                            this._listService.onEditorClick(item.id, false, item.data.id);
                        }
                    },
                    {
                        id: "rename-query",
                        data: item,
                        groupKey: "query-actions",
                        iconProps: { iconName: "Rename" },
                        text: Resources.RenameQueryTitle,
                        onActivate: (item: IMenuItem) => {
                            this._listService.onRenameItemClick(QueriesHubConstants.MoreButtonId + item.id, item.data, this._onDialogDismiss);
                        }
                    },
                    {
                        id: "delete-query",
                        data: item,
                        groupKey: "query-actions",
                        iconProps: { iconName: "Delete" },
                        text: Resources.DeleteQuery,
                        onActivate: (item: IMenuItem) => {
                            this._selectedQueryItem = item.data;
                            this._dialogOpen.value = DialogType.DeleteDialog;
                        }
                    },
                    {
                        id: "add-to-dashboard",
                        data: item,
                        groupKey: "add-to-dashboard-action",
                        text: Resources.AddToDashboardMenuItemTitle,
                        iconProps: { iconName: "Add" },
                        onActivate: () => {
                            this._dialogOpen.value = DialogType.AddToDashboard;
                        }
                    }
                );

                const existingTeams: IMenuItem[] = [];
                const teamsToBeAdded: IMenuItem[] = [];

                const teams = await this._getTeams();
                teams.forEach(team => {
                    const subMenu = {
                        id: team.id,
                        data: item,
                        text: team.name,
                        disabled: false,
                        groupName: QueriesHubConstants.FavoritesTab
                    } as IMenuItem;

                    const queryFavoriteGroup = this._listService.favoriteGroups.value.find(group => {
                        return group.id === team.id;
                    });

                    const queryFavorite = queryFavoriteGroup?.items.find(childItem => {
                        return childItem.id === item.id;
                    });

                    if (queryFavorite) {
                        subMenu.onActivate = (item: IMenuItem) => {
                            this._removTeamFavorite(item.data, team.id, team.name);
                        };
                        existingTeams.push(subMenu);
                    } else {
                        subMenu.onActivate = (item: IMenuItem) => {
                            this._addTeamFavorite(item.data, team.id, team.name);
                        };
                        teamsToBeAdded.push(subMenu);
                    }
                });

                if (existingTeams.length > 0) {
                    menuItems.push({
                        id: "remove-team-favorites-divider",
                        itemType: MenuItemType.Divider
                    });
                    menuItems.push({
                        id: "remove-from-team-favorites",
                        data: item,
                        groupKey: QueriesHubConstants.FavoritesTab,
                        text: Resources.RemoveFromTeamFavorites,
                        subMenuProps: {
                            id: "teams",
                            items: existingTeams
                        }
                    });
                }

                if (item.isPublic && teamsToBeAdded.length > 0) {
                    menuItems.push({
                        id: "add-team-favorites-divider",
                        itemType: MenuItemType.Divider
                    });
                    menuItems.push({
                        id: "add-to-team-favorites",
                        data: item,
                        groupKey: QueriesHubConstants.FavoritesTab,

                        text: Resources.AddToTeamFavorites,
                        subMenuProps: {
                            id: "teams",
                            items: teamsToBeAdded
                        }
                    });
                }
            }

            if (item.isPublic) {
                const securityMenuItem: IMenuItem = {
                    id: "item-security",
                    data: item,
                    text: Resources.SecurityTitle
                };
                securityMenuItem.onActivate = (item: IMenuItem) => {
                    this._showSecurityDialog(item.data);
                };

                menuItems.push(
                    {
                        id: "security-divider",
                        itemType: MenuItemType.Divider
                    },
                    securityMenuItem
                );
            }
        }

        this._menuItems.value = menuItems;
    };

    private _getTeams = async () => {
        if (!this._teams) {
            const teamService = this.context.pageContext.getService<ITfsTeamService>("ITfsTeamService");
            this._teams = await teamService.getMyTeams(this._projectId);
        }
        return this._teams;
    };

    private _onDialogDismiss = () => {
        this._dialogOpen.value = undefined;
        this._moreButton.current?.focus();
    };

    private _onDeleteItem = () => {
        this._listService.deleteQuery(this._selectedQueryItem);
    };

    private _showSecurityDialog = (queryItem: QueryItem) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const wrappedProps = {
                onCancel: () => {
                    onDismiss();
                },
                securityViewTitle: format(Resources.PermissionsTitle, queryItem.path),
                permissionSetId: SecurityNameSpaceIdForWorkItems,
                permissionSetToken: queryItem.id
            };

            return (
                <WrappedComponent
                    dependencies={["ms.vss-admin-web.security-view-common-content"]}
                    wrappedType="ms.vss-admin-web.security-panel-component"
                    wrappedProps={wrappedProps}
                />
            );
        });
    };

    private _updateMoreButton = (value: any, action: string) => {
        if (action === "push" || action === "pop") {
            const itemArray = value.changedItems[0];
            const item = itemArray.items[0];
            if (!item.isEmptyFolderContext && item.id === this.props.item.id) {
                this._getMenuPropsForItem();
                return true;
            }
        }
    };
}
